import {
  opsUserContactEmail,
  opsUserLoginMethods,
  type CustomerTransferCandidate,
  type OpsLoginMethod,
  type OpsUserRecord,
} from '@nadoutong/contracts';
import type { I18nKey } from '../i18n/index.ts';

type Translate = (key: I18nKey) => string;

/** Normalised identity used to label an ops user in a picker. */
export interface OpsUserPickerIdentity {
  readonly id: string;
  readonly displayName: string | null;
  /** `null` when the server did not report login methods (older build). */
  readonly loginMethods: readonly OpsLoginMethod[] | null;
  readonly contactEmail: string | null;
}

export const OPS_LOGIN_METHOD_LABEL_KEYS: Readonly<Record<OpsLoginMethod, I18nKey>> = {
  feishu: 'ops_user_identity.method.feishu',
  password: 'ops_user_identity.method.password',
  github: 'ops_user_identity.method.github',
};

/** Identity for an ops-user record returned by the ops-users list. */
export function opsUserPickerIdentityFromRecord(user: OpsUserRecord): OpsUserPickerIdentity {
  return {
    id: user.id,
    displayName: user.display_name,
    loginMethods: opsUserLoginMethods({
      feishu: user.feishu_open_id !== null,
      password: user.has_password,
      github: user.github_user_id !== null,
    }),
    contactEmail: opsUserContactEmail(user.email, user.feishu_enterprise_email),
  };
}

/** Identity for a transfer candidate, including older servers that omit the additive fields. */
export function opsUserPickerIdentityFromCandidate(
  candidate: CustomerTransferCandidate,
): OpsUserPickerIdentity {
  return {
    id: candidate.ops_user_id,
    displayName: candidate.display_name,
    loginMethods: candidate.login_methods ?? null,
    contactEmail:
      candidate.contact_email !== undefined
        ? candidate.contact_email
        : opsUserContactEmail(candidate.email, null),
  };
}

/** Localised login methods joined with '/', or null when unknown/empty. */
export function formatOpsLoginMethods(
  methods: readonly OpsLoginMethod[] | null | undefined,
  translate: Translate,
): string | null {
  if (methods === null || methods === undefined || methods.length === 0) {
    return null;
  }
  return methods.map((method) => translate(OPS_LOGIN_METHOD_LABEL_KEYS[method])).join('/');
}

function trimmedDisplayName(identity: OpsUserPickerIdentity): string {
  return identity.displayName?.trim() ?? '';
}

function displayNameKey(identity: OpsUserPickerIdentity): string {
  return trimmedDisplayName(identity).toLowerCase();
}

/** Ids whose trimmed, case-insensitive display name appears more than once in `identities`. */
export function duplicateDisplayNameIds(
  identities: readonly OpsUserPickerIdentity[],
): ReadonlySet<string> {
  const counts = new Map<string, number>();
  for (const identity of identities) {
    const key = displayNameKey(identity);
    if (key.length === 0) {
      continue;
    }
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const ids = new Set<string>();
  for (const identity of identities) {
    const key = displayNameKey(identity);
    if (key.length > 0 && (counts.get(key) ?? 0) > 1) {
      ids.add(identity.id);
    }
  }
  return ids;
}

/**
 * `name · methods · email [· idPrefix]` joined with ' · '. name = trimmed displayName,
 * else contactEmail, else id. Skip the email part when it is null or already used as the name,
 * skip methods when formatOpsLoginMethods returns null. idPrefix = id.slice(0, 8), only when
 * withIdPrefix is true.
 */
export function formatOpsUserPickerLabel(
  identity: OpsUserPickerIdentity,
  translate: Translate,
  options: { readonly withIdPrefix: boolean },
): string {
  const displayName = trimmedDisplayName(identity);
  const contactEmail = identity.contactEmail;
  const name =
    displayName.length > 0 ? displayName : contactEmail !== null && contactEmail.length > 0 ? contactEmail : identity.id;
  const parts: string[] = [name];
  const methods = formatOpsLoginMethods(identity.loginMethods, translate);
  if (methods !== null) {
    parts.push(methods);
  }
  if (contactEmail !== null && contactEmail.length > 0 && contactEmail !== name) {
    parts.push(contactEmail);
  }
  if (options.withIdPrefix) {
    parts.push(identity.id.slice(0, 8));
  }
  return parts.join(' · ');
}

/** One option per identity, adding the id prefix to members of a duplicate-name group. */
export function buildOpsUserPickerOptions(
  identities: readonly OpsUserPickerIdentity[],
  translate: Translate,
): { value: string; label: string }[] {
  const duplicateIds = duplicateDisplayNameIds(identities);
  return identities.map((identity) => ({
    value: identity.id,
    label: formatOpsUserPickerLabel(identity, translate, {
      withIdPrefix: duplicateIds.has(identity.id),
    }),
  }));
}
